import { useState, useEffect } from "react";
import { toast } from 'sonner';
import { Alert } from 'react-bootstrap';
// Services
import useSelectedDomain from "../../../service/Hooks/useSelectedDomain";
import { getOrdersByDomainId } from "../../../service/api/Order";
import { createTransaction } from '../../../service/api/TransactionService';
import { useUserData } from "../../../service/api/useUserData";
import { useLoader } from "../../../service/Hooks/useLoader";

// Composants
import OrderSelector from "../../Order/Selector/OrderSelector";
import FileUpload from '../FileUploads/FileUpload';
import TransactionSummary from "../transactionQuantite/TransactionSummary";
import PriceDisplay from "./components/PriceDisplay";
import QuantityInput from './components/QuantityInput';

const TransactionForm = ({ closeModal }) => {
  // États principaux
  const [orders, setOrders] = useState([]);
  const [selectedOrderId, setSelectedOrderId] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState("");
  const [creatorId, setCreatorId] = useState(null);
  const [preuve, setPreuve] = useState(null);
  // const [preuveError, setPreuveError] = useState(null);
  
  // États de validation et UI
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [maxQuantity, setMaxQuantity] = useState(0);
  const [quantityIsValid, setQuantityIsValid] = useState(true);
  
  // Hooks personnalisés
  const selectedDomain = useSelectedDomain();
  const { getUserInfo } = useUserData();
  const { showLoader, hideLoader } = useLoader();

  // Réinitialiser le formulaire quand le modal s'ouvre
  useEffect(() => {
    resetForm();
  }, []);

  // Fonction de fermeture robuste
  const handleClose = () => {
    resetForm();
    closeModal();
  };

  // Chargement des données initiales
  useEffect(() => {
    if (!selectedDomain?.id) return;

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        // Charger les ordres
        const fetchedOrders = await getOrdersByDomainId(selectedDomain.id);
        const filteredOrders = fetchedOrders.filter(
          (order) =>
            (order.targetMarketName === "Public" || order.targetMarketName === "Monetary") &&
            order.status === "Completed"
        );
        setOrders(filteredOrders);
        
        // Charger les infos utilisateur
        const userInfo = await getUserInfo();
        if (userInfo) {
          setCreatorId(userInfo.accountId);
        }
        
      } catch (error) {
        // console.error("Erreur lors du chargement des données:", error);
        toast.error("Desole , une erreur s'est produite lors du chargement des données lies aux titres");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [selectedDomain]);

  // Gestionnaire de sélection d'ordre
  const handleOrderChange = (selected) => {
    const orderId = selected?.id || "";
    setSelectedOrderId(orderId);

    if (selected) {
      setPrice(selected.price.toString());
      setMaxQuantity(selected.quantity);
      setQuantity("");
      setQuantityIsValid(true);
      setErrors(prev => ({ ...prev, orderId: undefined }));
    } else {
      setPrice("");
      setQuantity("");
      setMaxQuantity(0);
      setQuantityIsValid(true);
    }
  };

  // Gestionnaire de changement de quantité
  const handleQuantityChange = (event) => {
    const newQuantity = event.target.value;
    setQuantity(newQuantity);

    if (!newQuantity) {
      setErrors(prev => ({ ...prev, quantity: "La quantité est requise." }));
      setQuantityIsValid(false);
    } else if (isNaN(newQuantity) || parseInt(newQuantity) <= 0) {
      setErrors(prev => ({ ...prev, quantity: "La quantité doit être un nombre positif." }));
      setQuantityIsValid(false);
    } else if (parseInt(newQuantity) > maxQuantity) {
      setErrors(prev => ({ ...prev, quantity: `La quantité ne peut pas dépasser ${maxQuantity}.` }));
      setQuantityIsValid(false);
    } else {
      setErrors(prev => ({ ...prev, quantity: undefined }));
      setQuantityIsValid(true);
    }
  };

  // Gestionnaire de fichier preuve
  // const handlePreuveChange = (file, error) => {
  //   setPreuve(file);
  //   setPreuveError(error);
  // };
  const handlePreuveChange = (file) => {
    setPreuve(file);
  };

  // Validation du formulaire
  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    // Validation ordre
    if (!selectedOrderId) {
      newErrors.orderId = "Ordre est requis veuillez choisir l'ordre sur lequel vous souhaitez effectuer la transaction.";
      isValid = false;
    }
    // Validation quantité
    if (!quantity) {
      newErrors.quantity = "La quantité est requise.";
      isValid = false;
    } else if (isNaN(quantity) || parseInt(quantity) <= 0) {
      newErrors.quantity = "La quantité doit être un nombre positif.";
      isValid = false;
    } else if (parseInt(quantity) > maxQuantity) {
      newErrors.quantity = `La quantité ne peut pas dépasser ${maxQuantity}.`;
      isValid = false;
    }

    // Validation preuve (OBLIGATOIRE)
    if (!preuve) {
      newErrors.preuve = "La preuve est obligatoire pour créer une transaction.";
      isValid = false;
    }


    setErrors(newErrors);
    return isValid;
  };

  // Soumission du formulaire
  // Soumission du formulaire
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      toast.error("Veuillez corriger les erreurs dans le formulaire et recommencer");
      return;
    }

    try {
      const transactionData = {
        orderId: parseInt(selectedOrderId),
        creatorId: creatorId,
        price: parseFloat(price),
        quantity: parseInt(quantity),
        preuve: preuve
      };

      showLoader({ 
        message: "Création de la transaction en cours...", 
        indeterminate: true 
      });

      const result = await createTransaction(transactionData);

      toast.success(result.message || "Transaction créée avec succès!");

      handleClose();

    } catch (error) {

      // console.error("Erreur lors de la création de la transaction:", error);

      // Extraction intelligente du message backend
      const backendMessage =
        error.response?.data?.message ||     // Backend C#
        error.response?.data?.error ||       // Autre format possible backend
        error.message ||                      // Erreur transformée dans createTransaction()
        "Erreur interne lors de la création de la transaction";

      setErrors({ general: backendMessage });
      toast.error(backendMessage);
    } 
    finally {
      hideLoader();
    }
  };


  // Réinitialisation du formulaire
  const resetForm = () => {
    setSelectedOrderId("");
    setPrice("");
    setQuantity("");
    setPreuve(null);
    // setPreuveError(null);
    setMaxQuantity(0);
    setQuantityIsValid(true);
    setErrors({});
    
    // Réinitialiser également l'input fichier s'il existe
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  // Ordre sélectionné pour le résumé
  const selectedOrder = orders.find(order => order.id === parseInt(selectedOrderId));

  if (loading) {
    return (
      <div className="modal-header">
        <h5 className="modal-title">Créer une transaction</h5>
        <div className="text-center py-4">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-2 text-muted">Chargement des données...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="modal-header">
        <h5 className="modal-title">Créer une transaction</h5>
        <button
          type="button"
          className="btn-close"
          onClick={handleClose}
          aria-label="Close"
        ></button>
      </div>

      <div className="modal-body">
        {/* Message d'erreur général */}
        {errors.general && (
          <Alert variant="danger" className="mb-4">
            <strong>Erreur:</strong> {errors.general}
          </Alert>
        )}

        <form className="needs-validation" onSubmit={handleSubmit} noValidate>
          {/* Sélection de l'ordre */}
          <OrderSelector
            orders={orders}
            value={selectedOrderId}
            onChange={handleOrderChange}
            error={errors.orderId}
          />

          {/* Affichage du prix (lecture seule) */}
          <PriceDisplay value={price} />

          {/* Saisie de la quantité */}
          <QuantityInput
            value={quantity}
            onChange={handleQuantityChange}
            error={errors.quantity}
            isValid={quantityIsValid}
            maxQuantity={maxQuantity}
          />

          {/* Téléchargement de preuve (optionnel) */}
          <FileUpload
            value={preuve}
            onChange={handlePreuveChange}
            error={errors.preuve}
            className="mb-4"
          />

          {/* Résumé de la transaction */}
          {selectedOrder && (
            <TransactionSummary
              order={selectedOrder}
              quantity={quantity}
              price={price}
              hasPreuve={!!preuve}
            />
          )}

          {/* Champ caché pour creatorId */}
          <input type="hidden" name="creatorId" value={creatorId} />

          <hr className="mb-4" />
          
          {/* Boutons d'action */}
          <div className="d-flex gap-2 justify-content-end">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleClose}
            >
              Annuler
            </button>
            <button 
              className="btn btn-primary" 
              type="submit"
              disabled={
                !preuve ||
                Object.keys(errors).some(key => errors[key] && key !== 'general')
              }
            >
              Créer la transaction
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default TransactionForm;